import React, { useState, useContext, useEffect } from "react";
import {
  makeStyles,
  List,
  Typography,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
  Tooltip,
  Avatar,
  Drawer,
  AppBar,
  Toolbar,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";

import { useLocation } from "react-router-dom";

import MainListItems from "./MainListItems";
import SettingsPanel from "./SettingsPanel";
import NotificationsPopOver from "../components/NotificationsPopOver";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";

import { SocketContext } from "../context/Socket/SocketContext";

const sidebarWidth = 68;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    backgroundColor: theme.palette.fancyBackground,
    "& .MuiButton-outlinedPrimary": {
      color: "#fff",
      backgroundColor: theme.palette.primary.main,
    },
    "& .MuiTab-textColorPrimary.Mui-selected": {
      color: theme.palette.primary.main,
    },
  },
  sidebar: {
    width: sidebarWidth,
    minWidth: sidebarWidth,
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: theme.palette.sidebarBackground,
    borderRight: `1px solid ${theme.palette.borderPrimary}`,
    paddingTop: 10,
    paddingBottom: 10,
    zIndex: theme.zIndex.drawer + 1,
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  sidebarTop: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    flex: 1,
    width: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    ...theme.scrollbarStylesSoft,
  },
  sidebarBottom: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    width: "100%",
    paddingTop: 8,
    borderTop: `1px solid ${theme.palette.borderPrimary}`,
  },
  sidebarIcon: {
    color: theme.palette.sidebarIcon,
    padding: 10,
    borderRadius: 12,
    transition: "all 0.15s ease",
    "&:hover": {
      backgroundColor: theme.palette.sidebarHover,
    },
  },
  sidebarIconActive: {
    color: theme.palette.sidebarIconActive,
    backgroundColor: theme.palette.sidebarActive,
  },
  sidebarAvatar: {
    width: 36,
    height: 36,
    cursor: "pointer",
    border: `2px solid ${theme.palette.borderPrimary}`,
  },
  mainWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    height: "100vh",
  },
  content: {
    flex: 1,
    overflow: "auto",
  },
  containerWithScroll: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  // Mobile drawer
  mobileDrawer: {
    width: 260,
  },
  settingsOverlay: {
    position: 'fixed',
    left: sidebarWidth,
    top: 0,
    bottom: 0,
    width: 380,
    zIndex: theme.zIndex.drawer,
    transform: 'translateX(-120%)',
    transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    [theme.breakpoints.down("xs")]: {
      left: 0,
      width: "100%",
    },
  },
  settingsOverlayOpen: {
    transform: 'translateX(0)',
    boxShadow: '2px 0 12px rgba(0,0,0,0.08)',
  },
}));

const LoggedInLayout = ({ children, themeToggle }) => {
  const classes = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const { user } = useContext(AuthContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const location = useLocation();

  const [volume, setVolume] = useState(localStorage.getItem("volume") || 1);

  const socketManager = useContext(SocketContext);

  // Close settings panel on navigation
  useEffect(() => {
    setSettingsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const userId = localStorage.getItem("userId");

    const socket = socketManager.getSocket(companyId);

    const handleAuth = (data) => {
      if (data.user.id === +userId) {
        toastError("Sua conta foi acessada em outro computador.");
        setTimeout(() => {
          localStorage.clear();
          window.location.reload();
        }, 1000);
      }
    };

    socket.on(`company-${companyId}-auth`, handleAuth);

    socket.emit("userStatus");
    const interval = setInterval(() => {
      socket.emit("userStatus");
    }, 1000 * 60 * 5);

    return () => {
      socket.off(`company-${companyId}-auth`, handleAuth);
      clearInterval(interval);
    };
  }, [socketManager]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  if (loading) {
    return <BackdropLoading />;
  }

  const sidebarContent = (
    <>
      <div className={classes.sidebarTop}>
        {/* Main navigation icons */}
        <MainListItems drawerClose={() => setMobileOpen(false)} collapsed={true} onSettingsClick={() => setSettingsOpen(true)} />
      </div>

      <div className={classes.sidebarBottom}>
        {/* Unified notification bell */}
        <NotificationsPopOver volume={volume} setVolume={setVolume} />

        {/* User avatar at bottom */}
        <Tooltip title={user?.name || ""} placement="right">
          <Avatar
            className={classes.sidebarAvatar}
            onClick={handleMenu}
            src={user?.profileImage}
          >
            {user?.name?.charAt(0)?.toUpperCase()}
          </Avatar>
        </Tooltip>
      </div>
    </>
  );

  return (
    <div className={classes.root}>
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={menuOpen}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={handleOpenUserModal}>
          {i18n.t("mainDrawer.appBar.user.profile")}
        </MenuItem>
        <MenuItem onClick={handleClickLogout}>
          {i18n.t("mainDrawer.appBar.user.logout")}
        </MenuItem>
      </Menu>

      {/* Desktop sidebar */}
      {!isMobile && (
        <div className={classes.sidebar}>
          {sidebarContent}
        </div>
      )}

      {/* Settings Panel Overlay */}
      <div className={`${classes.settingsOverlay} ${settingsOpen ? classes.settingsOverlayOpen : ''}`}>
        <SettingsPanel onClose={() => setSettingsOpen(false)} visible={settingsOpen} />
      </div>

      {/* Mobile drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          classes={{ paper: classes.mobileDrawer }}
        >
          <List>
            <MainListItems
              drawerClose={() => setMobileOpen(false)}
              collapsed={false}
              onSettingsClick={() => setSettingsOpen(true)}
              onProfileClick={() => { handleOpenUserModal(); setMobileOpen(false); }}
            />
          </List>
        </Drawer>
      )}

      <div className={classes.mainWrapper}>
        {isMobile && (
          <AppBar position="static" elevation={1} style={{ backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, zIndex: 10 }}>
            <Toolbar variant="dense">
              <IconButton edge="start" color="inherit" aria-label="menu" onClick={() => setMobileOpen(true)}>
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" className={classes.title}>
                {process.env.REACT_APP_NAME_SYSTEM || "PROSIPE Chat"}
              </Typography>
            </Toolbar>
          </AppBar>
        )}
        <main className={classes.content}>
          {children ? children : null}
        </main>
      </div>
    </div>
  );
};

export default LoggedInLayout;